import { call, fork, put, take, race, select } from 'redux-saga/effects';
import { takeEvery, takeLatest } from 'redux-saga';
import * as restartActions from 'components/widgets/Restart/actions';
import ModelsClient from 'rest/ModelsClient';
import EndpointsClient from 'rest/EndpointsClient';
import { errorAction } from 'components/utility/actions';
import browserHistory from 'components/utility/history';
import * as connectorActions from 'components/pages/connectors/actions';
import * as actions from './actions';
import ModelOps from './view/ModelOps';

const modelsClient = new ModelsClient();
const endpointsClient = new EndpointsClient();

export function doFetch() {
	return modelsClient.getAll();
}

export function doSave(action) {
	return modelsClient.create(action.payload);
}

export function redirectToModelListView() {
	browserHistory.push(`${window.$conf.prefix}/project/models`);
}

export function doGenerateEndpoints(action) {
	return endpointsClient.generateEndpoints({
		model: action.payload,
		name: action.payload
	});
}

export function *fetchConnectors() {
	const connectors = yield select(
		state => state.connectors && state.connectors.connectors
	);

	if (!connectors) {
		return yield [
			race({
				success: take(connectorActions.FETCH_CONNECTORS_SUCCESS),
				error: take(connectorActions.FETCH_CONNECTORS_ERROR)
			}),
			put({ type: connectorActions.FETCH_CONNECTORS })
		];
	} else {
		return yield [];
	}
}

export function *saveModel(action) {
	try {
		const response = yield call(doSave, action);
		yield put({ type: actions.SAVE_MODEL_SUCCESS, payload: response });
	} catch (error) {
		yield put(errorAction(actions.SAVE_MODEL_ERROR, error, action));
		return;
	}

	// Wait for the restart & redirect
	const [ result ] = yield [
		race({
			success: take(restartActions.AWAIT_RESTART_SUCCESS),
			error: take(restartActions.AWAIT_RESTART_ERROR),
			timeout: take(restartActions.AWAIT_RESTART_TIMEOUT)
		}),
		put(restartActions.awaitRestart())
	];

	if (result && result.success) {
		yield call(redirectToModelListView);
	}
}

export function *fetchModels(action) {
	try {
		const [ connRes, modelRes ] = yield [
			fetchConnectors(),
			call(doFetch)
		];
		if (connRes && connRes.error) {
			yield put({ type: actions.DESIGN_MODEL_ERROR, payload: connRes.error.payload });
			return;
		}

		// add metadata to models for join info
		const models = modelRes.data || {};
		Object.keys(models).forEach(modelName => {
			if (models.hasOwnProperty(modelName)) {
				ModelOps.updateMetadata(models[modelName]);
			}
		});
		yield put({ type: actions.FETCH_MODELS_SUCCESS, payload: models });
	} catch (error) {
		yield put(errorAction(actions.FETCH_MODELS_ERROR, error, action));
	}
}

export function *fetchModel(action) {
	try {
		// If we already have all models then just use that
		let models = yield select(
			state => state.models && state.models.models
		);

		if (!models || !models[action.payload]) {
			// Loading all the models for now
			yield call(fetchModels, action);
			models = yield select(
				state => state.models && state.models.models
			);
		}
		// Loaded the new models fine
		if (models && models[action.payload]) {
			yield put({ type: actions.FETCH_MODEL_SUCCESS, payload: models[action.payload] });
		} else {
			// Not found
			yield put(errorAction(actions.FETCH_MODEL_ERROR, { status: 404, statusText: 'Not found' }, action));
		}
	} catch (error) {
		yield put(errorAction(actions.FETCH_MODEL_ERROR, error, action));
	}
}

export function *designModel(action) {
	try {
		//
		// Need the connectors
		//
		const [ connResult ] = yield* fetchConnectors();
		if (connResult && connResult.error) {
			yield put({ type: actions.DESIGN_MODEL_ERROR, payload: connResult.error.payload });
			return;
		}

		//
		// Need the models
		//
		let models = yield select(
			state => state.models && state.models.models
		);

		if (!models) {
			const [ result ] = yield [
				race({
					success: take(actions.FETCH_MODELS_SUCCESS),
					error: take(actions.FETCH_MODELS_ERROR)
				}),
				put({ type: actions.FETCH_MODELS })
			];

			if (result.error) {
				yield put({ type: actions.DESIGN_MODEL_ERROR, payload: result.error.payload });
				return;
			}

			models = yield select(
				state => state.models && state.models.models
			);
		}

		//
		// Emit the state
		//
		yield put({
			type: actions.DESIGN_MODEL_SUCCESS,
			payload: action.payload
		});
	} catch (e) {
		yield put(errorAction(actions.DESIGN_MODEL_ERROR, e, action));
	}
}

export function *generateEndpoints(action) {
	if (!action.payload) {
		yield put(errorAction(actions.GENERATE_ENDPOINTS_ERROR, { message: 'model name was not provided' }, action));
		return;
	}

	yield put({ type: actions.GENERATE_ENDPOINTS_STARTED });

	try {
		const response = yield call(doGenerateEndpoints, action);
		const [ result ] = yield [
			race({
				success: take(restartActions.AWAIT_RESTART_SUCCESS),
				error: take(restartActions.AWAIT_RESTART_ERROR),
				timeout: take(restartActions.AWAIT_RESTART_TIMEOUT)
			}),
			put(restartActions.awaitRestart())
		];

		if (result && result.success) {
			yield put({ type: actions.GENERATE_ENDPOINTS_SUCCESS, payload: response });
		}
	} catch (error) {
		yield put(errorAction(actions.GENERATE_ENDPOINTS_ERROR, error, action));
	}
}

export default function *sagas() {
	yield fork(takeEvery, actions.FETCH_MODEL, fetchModel);
	yield fork(takeLatest, actions.FETCH_MODELS, fetchModels);
	yield fork(takeLatest, actions.SAVE_MODEL, saveModel);
	yield fork(takeLatest, actions.DESIGN_MODEL, designModel);
	yield fork(takeLatest, actions.GENERATE_ENDPOINTS, generateEndpoints);
}
